import { useState } from 'react';
import { motion } from 'framer-motion';
import { Send, MapPin, Mail, Phone, MessageSquare, Loader2, CheckCircle } from 'lucide-react';
import { AnimatedSection } from '@/components/AnimatedComponents';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export default function Contact() {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    subject: '',
    message: '',
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState(null);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitStatus(null);

    try {
      await axios.post(`${BACKEND_URL}/api/contact`, formData);
      setSubmitStatus('success');
      setFormData({ name: '', email: '', subject: '', message: '' });
    } catch (error) {
      setSubmitStatus('error');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <main data-testid="contact-page">
      {/* Hero */}
      <section className="pt-32 pb-20 md:pt-40 md:pb-24 bg-[#050505]">
        <div className="container-wide">
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="caption mb-4"
          >
            Get in Touch
          </motion.p>
          <motion.h1
            initial={{ opacity: 0, y: 40 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
            className="text-5xl md:text-6xl lg:text-8xl font-bold tracking-tighter mb-6"
          >
            Let&apos;s<br />
            <span className="text-[#007AFF]">Connect</span>
          </motion.h1>
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
            className="text-[#A1A1AA] text-lg md:text-xl max-w-xl"
          >
            Ready to discuss your next project? Get in touch and let&apos;s 
            create something extraordinary together.
          </motion.p>
        </div>
      </section>

      {/* Contact Form & Info */}
      <section className="py-24 md:py-32 bg-[#050505]" data-testid="contact-form-section">
        <div className="container-wide">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16">
            {/* Contact Form */}
            <AnimatedSection>
              <h2 className="text-3xl font-bold mb-8">Send a Message</h2>
              <form onSubmit={handleSubmit} className="space-y-8" data-testid="contact-form">
                <div>
                  <label htmlFor="name" className="form-label">Your Name</label>
                  <input
                    type="text"
                    id="name"
                    name="name"
                    value={formData.name}
                    onChange={handleChange}
                    required
                    className="form-input"
                    placeholder="John Smith"
                    data-testid="contact-name-input"
                  />
                </div>

                <div>
                  <label htmlFor="email" className="form-label">Email Address</label>
                  <input
                    type="email"
                    id="email"
                    name="email"
                    value={formData.email}
                    onChange={handleChange}
                    required
                    className="form-input"
                    placeholder="john@example.com"
                    data-testid="contact-email-input"
                  />
                </div>

                <div>
                  <label htmlFor="subject" className="form-label">Subject</label>
                  <input
                    type="text"
                    id="subject"
                    name="subject"
                    value={formData.subject}
                    onChange={handleChange}
                    required
                    className="form-input"
                    placeholder="Project inquiry"
                    data-testid="contact-subject-input"
                  />
                </div>

                <div>
                  <label htmlFor="message" className="form-label">Message</label>
                  <textarea
                    id="message"
                    name="message"
                    value={formData.message}
                    onChange={handleChange}
                    required
                    rows={6}
                    className="form-input resize-none"
                    placeholder="Tell me about your project..."
                    data-testid="contact-message-input"
                  />
                </div>

                <button
                  type="submit"
                  disabled={isSubmitting}
                  className="btn-primary w-full flex items-center justify-center gap-2"
                  data-testid="contact-submit-button"
                >
                  {isSubmitting ? (
                    <>
                      <Loader2 className="w-5 h-5 animate-spin" />
                      Sending...
                    </>
                  ) : (
                    <>
                      <Send size={18} />
                      Send Message
                    </>
                  )}
                </button>

                {submitStatus === 'success' && (
                  <div className="flex items-center gap-3 p-4 bg-green-500/10 border border-green-500/30 text-green-400" data-testid="contact-success-message">
                    <CheckCircle size={20} />
                    <p>Message sent successfully! I&apos;ll get back to you soon.</p>
                  </div>
                )}

                {submitStatus === 'error' && (
                  <div className="p-4 bg-red-500/10 border border-red-500/30 text-red-400" data-testid="contact-error-message">
                    <p>Something went wrong. Please try again or email me directly.</p>
                  </div>
                )}
              </form>
            </AnimatedSection>

            {/* Contact Info & Placeholders */}
            <div className="space-y-12">
              <AnimatedSection delay={0.1}>
                <h2 className="text-3xl font-bold mb-8">Contact Info</h2>
                <div className="space-y-6">
                  <div className="flex items-start gap-4">
                    <div className="w-12 h-12 flex items-center justify-center border border-[#27272A]">
                      <MapPin className="w-5 h-5 text-[#007AFF]" />
                    </div>
                    <div>
                      <p className="font-bold mb-1">Location</p>
                      <p className="text-[#A1A1AA]">Sydney, Australia</p>
                      <p className="text-[#52525B] text-sm">Available worldwide</p>
                    </div>
                  </div>

                  <div className="flex items-start gap-4">
                    <div className="w-12 h-12 flex items-center justify-center border border-[#27272A]">
                      <Mail className="w-5 h-5 text-[#007AFF]" />
                    </div>
                    <div>
                      <p className="font-bold mb-1">Email</p>
                      <a 
                        href="mailto:email@terrywebster.com.au" 
                        className="text-[#A1A1AA] hover:text-[#007AFF] transition-colors"
                      >
                        email@terrywebster.com.au
                      </a>
                    </div>
                  </div>

                  <div className="flex items-start gap-4">
                    <div className="w-12 h-12 flex items-center justify-center border border-[#27272A]">
                      <Phone className="w-5 h-5 text-[#007AFF]" />
                    </div>
                    <div>
                      <p className="font-bold mb-1">Phone</p>
                      <p className="text-[#A1A1AA]">Available upon request</p>
                    </div>
                  </div>
                </div>
              </AnimatedSection>

              {/* Chatbot Info */}
              <AnimatedSection delay={0.2}>
                <h3 className="text-xl font-bold mb-4 flex items-center gap-2">
                  <MessageSquare className="w-5 h-5 text-[#007AFF]" />
                  Book a Call
                </h3>
                <div 
                  className="p-6 border border-[#27272A] bg-[#0C0C0C] rounded-sm"
                  data-testid="chatbot-info"
                >
                  <p className="text-[#A1A1AA] mb-3">
                    Click the chat button in the bottom right corner to talk to Terry's AI assistant and schedule a call.
                  </p>
                  <p className="text-[#52525B] text-sm">
                    Available 24/7 to answer questions about services, experience, and booking.
                  </p>
                </div>
              </AnimatedSection>
            </div>
          </div>
        </div>
      </section>

      {/* Map or Final CTA */}
      <section className="py-24 bg-[#0C0C0C] border-t border-[#27272A]">
        <div className="container-wide text-center">
          <AnimatedSection>
            <p className="text-[#A1A1AA] text-lg mb-4">
              Based in Sydney, working globally
            </p>
            <h2 className="text-3xl md:text-4xl font-bold tracking-tighter">
              Let&apos;s bring your vision to life
            </h2>
          </AnimatedSection>
        </div>
      </section>
    </main>
  );
}
